import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';

interface EditPaymentIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  integrationId: number | null;
}

const EditPaymentIntegrationModal: React.FC<EditPaymentIntegrationModalProps> = ({
  open,
  onClose,
  onSaved,
  integrationId,
}) => {
  const { branches } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [provider, setProvider] = useState<string>('paytr');
  const [branchId, setBranchId] = useState<string>('');
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);
  const [is3DEnabled, setIs3DEnabled] = useState<boolean>(true);

  const [merchantId, setMerchantId] = useState<string>('');
  const [merchantKey, setMerchantKey] = useState<string>('');
  const [merchantSalt, setMerchantSalt] = useState<string>('');
  const [showMerchantKey, setShowMerchantKey] = useState<boolean>(false);
  const [showMerchantSalt, setShowMerchantSalt] = useState<boolean>(false);

  useEffect(() => {
    if (!open || !integrationId) return;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await mainApi.get(`/api/paymentproviderintegration/${integrationId}`);
        const result = response.data;
        if (result.Success === false || result.success === false) {
          throw new Error(result.Message || result.message || 'Kayıt yüklenemedi.');
        }
        const d = result.Data || result.data;
        setProvider(d.Provider ?? d.provider ?? 'paytr');
        setBranchId((d.BranchId ?? d.branchId) || '');
        setIsEnabled(!!(d.IsEnabled ?? d.isEnabled));
        setIsTestMode(!!(d.IsTestMode ?? d.isTestMode));
        setIs3DEnabled(!!(d.Is3DEnabled ?? d.is3DEnabled));
        setMerchantId((d.MerchantId ?? d.merchantId) || '');
        setMerchantKey((d.MerchantKey ?? d.merchantKey) || '');
        setMerchantSalt((d.MerchantSalt ?? d.merchantSalt) || '');
        setShowMerchantKey(false);
        setShowMerchantSalt(false);
      } catch (err: any) {
        setError(
          err?.response?.data?.Message ||
            err?.response?.data?.message ||
            err?.message ||
            'Kayıt yüklenirken bir hata oluştu.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [open, integrationId]);

  const handleSubmit = async () => {
    if (!integrationId) return;
    setError(null);

    if (provider === 'paytr') {
      if (!merchantId.trim()) {
        setError('Merchant ID zorunludur.');
        return;
      }
      if (!merchantKey.trim()) {
        setError('Merchant Key zorunludur.');
        return;
      }
      if (!merchantSalt.trim()) {
        setError('Merchant Salt zorunludur.');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        Id: integrationId,
        BranchId: branchId.trim() || null,
        IsEnabled: isEnabled,
        IsTestMode: isTestMode,
        Is3DEnabled: is3DEnabled,
        MerchantId: merchantId.trim() || null,
        MerchantKey: merchantKey.trim() || null,
        MerchantSalt: merchantSalt.trim() || null,
        ClientCode: null,
        ClientUsername: null,
        ClientPassword: null,
        Guid: null,
      };

      const response = await mainApi.post('/api/paymentproviderintegration/update', payload);
      const result = response.data;

      if (result.Success === false || result.success === false) {
        throw new Error(result.Message || result.message || 'Kayıt güncellenemedi.');
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        'Kayıt güncellenirken bir hata oluştu.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Ödeme Entegrasyonu Düzenle
          </Typography>
          <Chip label={provider.toUpperCase()} size="small" color="primary" />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="edit-branch-select-label">Şube (boş bırakılırsa şirket geneli)</InputLabel>
              <Select
                labelId="edit-branch-select-label"
                value={branchId}
                label="Şube (boş bırakılırsa şirket geneli)"
                onChange={(e) => setBranchId(e.target.value as string)}
              >
                <MenuItem value="">
                  <em>Şirket Geneli (tüm şubeler)</em>
                </MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1 }}>
              <Chip label="Durum" size="small" />
            </Divider>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    color="success"
                  />
                }
                label="Aktif"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isTestMode}
                    onChange={(e) => setIsTestMode(e.target.checked)}
                    color="warning"
                  />
                }
                label="Test Modu"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={is3DEnabled}
                    onChange={(e) => setIs3DEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="3D Secure"
              />
            </Box>

            {provider === 'paytr' && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Chip label="PayTR Bilgileri" color="primary" size="small" />
                </Divider>

                <TextField
                  label="Merchant ID"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="off"
                />

                <TextField
                  label="Merchant Key"
                  value={merchantKey}
                  onChange={(e) => setMerchantKey(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="new-password"
                  type={showMerchantKey ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowMerchantKey((v) => !v)}
                          edge="end"
                        >
                          {showMerchantKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Merchant Salt"
                  value={merchantSalt}
                  onChange={(e) => setMerchantSalt(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="new-password"
                  type={showMerchantSalt ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowMerchantSalt((v) => !v)}
                          edge="end"
                        >
                          {showMerchantSalt ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Kaydediliyor...' : 'Güncelle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPaymentIntegrationModal;
